<template>
  <EditDialog
    v-if="modifiedEvent"
    ref="editDialog"
    mode="edit"
    :event="modifiedEvent"
    :errors="errors"
    :loading="loading"
    :without-client="withoutClient"
    :has-updated-rule="hasUpdatedRule"
    :has-deleted-rule="hasDeletedRule"
    :has-future-rule="hasFutureRule"
    :send-confirmation-email-default="false"
    @save="onClickSave"
    @hide="modifiedEvent = null"
    @close="(func) => func()"
  />

  <message-dialog
    v-model="confirmDialog.show"
    title="Confirm"
  >
    <!-- If rrule was deleted from this appointment onwards -->
    <template v-if="hasDeletedRule">
      <q-item-label>You have deleted the recurring rule from this appointment.</q-item-label>
      <q-item-label class="text-bold">
        Any future appointments which were part of this rule will be deleted.
      </q-item-label>
    </template>
    <!-- If the rrule was updated -->
    <template v-else-if="hasUpdatedRule">
      <q-item-label>You have created a new recurring rule for this appointment.</q-item-label>
      <q-item-label class="text-bold">
        Any scheduled and unscheduled appointments after this appointment date will be deleted.
      </q-item-label>
    </template>
    <!-- If the rrule was not touched -->
    <template v-else>
      <q-item-label>This appointment is part of an existing recurring rule.</q-item-label>
      <q-separator class="q-my-sm" />
      <q-item-label class="text-bold">
        Do you want to save your changes across other future recurring appointments?
      </q-item-label>
      <div>
        <q-option-group
          v-model="confirmDialog.updateType"
          :options="updateOptions"
          type="radio"
        />
      </div>
    </template>
    <template #actions>
      <q-btn
        v-close-popup
        flat
        label="Cancel"
      />
      <q-btn
        v-if="hasDeletedRule || hasUpdatedRule"
        v-close-popup
        flat
        autofocus
        label="Continue"
        color="orange-8"
        @click="finish('scheduledAndUnscheduled')"
      />
      <q-btn
        v-else
        v-close-popup
        flat
        autofocus
        label="Continue"
        color="orange-8"
        @click="finish()"
      />
    </template>
  </message-dialog>
</template>

<script>
import EditDialog from './partials/edit/Dialog'
import eventSenderMixin from './partials/mixins/io/eventSenderMixin'

export default {
  components: {
    EditDialog
  },
  mixins: [eventSenderMixin],
  props: {
    event: {
      type: Object,
      required: true
    },
    withoutClient: {
      type: Boolean,
      default: false
    }
  },
  emits: ['updated'],
  data () {
    return {
      modifiedEvent: null,
      errors: {},
      loading: false,
      sendConfirmationEmailPending: false,
      confirmDialog: {
        show: false,
        updateType: 'none'
      },
      updateOptions: [
        { label: 'Only update this. Do not update recurring appointments', value: 'none' },
        { label: 'Update unscheduled appointments after this appointment date', value: 'unscheduled' },
        { label: 'Update scheduled and unscheduled appointments after this appointment date', value: 'scheduledAndUnscheduled' }
      ]
    }
  },
  computed: {
    // A rule was updated to a future appointment, hence removing the rule object from this appointment
    hasFutureRule () {
      return this.event && this.event.is_recurring && !this.event.recursion_rule
    },
    // THe rule for this appointment has been modified
    hasUpdatedRule () {
      return this.event && this.event.recursion_rule && this.modifiedEvent && this.modifiedEvent.recursion_rule && ((this.modifiedEvent.recursion_rule.value !== this.event.recursion_rule.value) || (this.event.recurring_status !== this.modifiedEvent.recurring_status))
    },
    // The rule for this appointment has been deleted
    hasDeletedRule () {
      return this.event && this.event.recursion_rule && this.modifiedEvent && !this.modifiedEvent.recursion_rule
    }
  },
  methods: {
    async open () {
      this.modifiedEvent = JSON.parse(JSON.stringify(this.event))
      this.$nextTick(() => {
        this.$refs.editDialog.open()
      })
    },
    onClickSave (options = {}) {
      this.sendConfirmationEmailPending = options.sendConfirmationEmail || false
      if (this.modifiedEvent.is_recurring) {
        this.confirmDialog.show = true
      }
      else {
        this.finish()
      }
    },
    async finish (forceVal) {
      const updateRecurring = forceVal || this.confirmDialog.updateType
      this.errors = {}
      this.loading = true

      const event = this.modifiedEvent
      event.start_date = this.luxon.fromSQL(event.start_date).toSimpleSQL()
      event.end_date = this.luxon.fromSQL(event.end_date).toSimpleSQL()

      const { status, payload, errors } = await this.$put('v1/appointments', {
        id: event.id,
        update_recurring: updateRecurring,
        reference_id: event.reference_id,
        start_date: event.start_date,
        end_date: event.end_date,
        timezone: event.timezone,
        title: event.title,
        description: event.description,
        address: event.address,
        attendees: event.attendees.map(item => item.id),
        clients: event.clients.map(item => item.id),
        all_day: event.all_day,
        color: event.color,
        status: event.status,
        types: event.types.map(item => item.id),
        reminders: [...event.client_reminders, ...event.staff_reminders, ...event.unscheduled_reminders],
        recursion_rule: event.recursion_rule ? event.recursion_rule.value : null,
        recurring_status: event.recurring_status,
        required_form_template_ids: event.required_form_template_ids || [],
        checklist_template_ids: event.checklist_template_ids || [],
        send_confirmation_email: this.sendConfirmationEmailPending
      })
      this.loading = false
      if (status === 'success') {
        this.$q.notify({
          color: 'green',
          message: 'Appointment successfully updated'
        })
        this.ioEventUpdate(event.id)
        this.$emit('updated', payload)
      }
      else {
        this.errors = errors
        this.$nextTick(() => { this.scrollToFirstError(this.$el) })
      }
    }
  }
}
</script>
